import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { PlanLimitError } from '../../../features/plans/models/enforcement.model';
import { PlanLimitErrorService } from '../../../features/plans/services/plan-limit-error.service';
import { PlanLimitStateService } from '../../../features/plans/services/plan-limit-state.service';
import { RoleDTO, RolesService } from '../../../modules/admin/services/roles.service';
import { DocumentService } from '../../../services/document.service';
import { FrontendLoggerService } from '../../../services/frontend-logger.service';
import { LanguageService } from '../../../services/language/language.service';
import { NotificationService } from '../../../services/notification.service';
import { ThemeService } from '../../../services/theme/theme.service';
import { BreadcrumbInputComponent } from './breadcrumb-input/breadcrumb-input.component';
import { UploadAccessCoordinator } from './upload-access.coordinator';
import { UploadFileSelectionCoordinator } from './upload-file-selection.coordinator';
import { UploadLifecycleCallbacks, UploadLifecycleCoordinator } from './upload-lifecycle.coordinator';
import { DocumentTypePresentation, UploadFile } from './upload.models';

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.shell-and-dropzone.scss', './upload.component.file-list-and-responsive.scss'],
  standalone: false
})
export class UploadComponent implements OnInit, AfterViewInit {
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('dropZone', { static: false }) dropZone!: ElementRef<HTMLElement>;
  @ViewChild(BreadcrumbInputComponent) breadcrumbInputComponent!: BreadcrumbInputComponent;

  uploadForm: FormGroup;
  files: UploadFile[] = [];
  isUploading = false;
  currentTag = '';
  planLimitError: PlanLimitError | null = null;
  roles: RoleDTO[] = [];
  rolesLoading = false;
  rolesError = '';
  documentTypes: Record<string, DocumentTypePresentation> = {
    pdf: { extension: 'pdf', icon: 'fa-file-pdf', color: '#e53935' },
    docx: { extension: 'docx', icon: 'fa-file-word', color: '#1565c0' },
    doc: { extension: 'doc', icon: 'fa-file-word', color: '#1565c0' },
    txt: { extension: 'txt', icon: 'fa-file-alt', color: '#607d8b' },
    csv: { extension: 'csv', icon: 'fa-file-csv', color: '#43a047' },
    json: { extension: 'json', icon: 'fa-file-code', color: '#f57c00' },
    md: { extension: 'md', icon: 'fa-file-code', color: '#7b1fa2' },
    xlsx: { extension: 'xlsx', icon: 'fa-file-excel', color: '#388e3c' },
    xls: { extension: 'xls', icon: 'fa-file-excel', color: '#388e3c' },
    xlsm: { extension: 'xlsm', icon: 'fa-file-excel', color: '#388e3c' },
    pptx: { extension: 'pptx', icon: 'fa-file-powerpoint', color: '#d84315' },
    ppt: { extension: 'ppt', icon: 'fa-file-powerpoint', color: '#d84315' }
  };

  private readonly selection: UploadFileSelectionCoordinator;
  private readonly access: UploadAccessCoordinator;
  private readonly lifecycle: UploadLifecycleCoordinator;

  constructor(
    fb: FormBuilder,
    documentService: DocumentService,
    frontendLogger: FrontendLoggerService,
    public readonly themeService: ThemeService,
    public readonly languageService: LanguageService,
    dialog: MatDialog,
    rolesService: RolesService,
    planLimitErrorService: PlanLimitErrorService,
    planLimitState: PlanLimitStateService,
    notificationService: NotificationService
  ) {
    this.uploadForm = fb.group({
      processImmediately: [true],
      folderName: ['', Validators.maxLength(50)],
      tags: [[]],
      roleId: ['', Validators.required]
    });
    this.selection = new UploadFileSelectionCoordinator(frontendLogger);
    this.access = new UploadAccessCoordinator(
      rolesService, planLimitState, notificationService, frontendLogger
    );
    this.lifecycle = new UploadLifecycleCoordinator(
      documentService, dialog, planLimitErrorService, frontendLogger
    );
  }

  ngOnInit(): void { this.loadRoles(); }
  ngAfterViewInit(): void { this.setupDragAndDrop(); }

  setupDragAndDrop(): void {
    this.selection.setupDropZone(this.dropZone?.nativeElement, (files) => this.handleFiles(files));
  }

  openFileDialog(): void { this.fileInput.nativeElement.click(); }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
    }
  }

  handleFiles(fileList: FileList): void {
    const tags = this.uploadForm.get('tags')?.value || [];
    this.files.push(...this.selection.select(fileList, this.documentTypes, tags));
    this.fileInput.nativeElement.value = '';
  }

  removeFile(fileId: string): void { this.files = this.files.filter((file) => file.id !== fileId); }
  hasCompletedFiles(): boolean { return this.files.some((file) => file.status === 'created'); }

  addTag(): void {
    const tag = this.currentTag.trim();
    const currentTags = this.uploadForm.get('tags')?.value || [];
    if (tag && !currentTags.includes(tag)) {
      this.uploadForm.patchValue({ tags: [...currentTags, tag] });
    }
    this.currentTag = '';
  }

  removeTag(tag: string): void {
    const tags = this.uploadForm.get('tags')?.value || [];
    this.uploadForm.patchValue({ tags: tags.filter((item: string) => item !== tag) });
  }

  removeTagFromFile(fileId: string, tag: string): void {
    const file = this.files.find((item) => item.id === fileId);
    if (file) {
      file.tags = file.tags.filter((item) => item !== tag);
    }
  }

  getFileIcon(fileName: string): string { return this.filePresentation(fileName)?.icon || 'fa-file'; }
  getFileColor(fileName: string): string { return this.filePresentation(fileName)?.color || '#607d8b'; }
  translate(key: string): string { return this.languageService.translate(key); }

  loadRoles(): void {
    this.rolesLoading = true;
    this.rolesError = '';
    this.access.loadRoles().subscribe({
      next: (roles) => {
        this.roles = this.access.activeRoles(roles);
        this.rolesLoading = false;
        this.access.logLoaded(this.roles.length, false);
      },
      error: (error) => {
        this.rolesError = 'Error al cargar roles';
        this.access.logLoadError(error);
        this.initializeRoles();
      }
    });
  }

  initializeRoles(): void {
    this.roles = this.access.fallbackRoles();
    this.rolesLoading = false;
    this.access.logLoaded(this.roles.length, true);
  }

  get isUploadBlocked(): boolean { return this.access.isBlocked(); }
  dismissPlanLimit(): void { this.planLimitError = null; }

  showPlanLimit(error: PlanLimitError): void {
    this.planLimitError = this.access.showLimit(error, this.planLimitFriendlyMessage());
  }

  planLimitFriendlyMessage(): string { return this.translate('enforcement.banner.body'); }

  uploadFiles(): void {
    if (this.files.length === 0 || this.isUploading || this.isUploadBlocked) {
      return;
    }
    this.planLimitError = null;
    this.isUploading = true;
    this.files.filter((file) => file.status === 'pending').forEach((file) => this.uploadFile(file));
  }

  uploadFile(file: UploadFile): void {
    this.isUploading = true;
    this.lifecycle.upload(file, {
      folderId: this.breadcrumbInputComponent.getCurrentFolderId(),
      processImmediately: this.uploadForm.get('processImmediately')?.value,
      folderName: this.uploadForm.get('folderName')?.value || '',
      roleId: this.uploadForm.get('roleId')?.value
    }, this.lifecycleCallbacks());
  }

  replaceFile(file: UploadFile): void {
    this.isUploading = true;
    this.lifecycle.replace(
      file,
      this.uploadForm.get('roleId')?.value,
      this.uploadForm.get('folderName')?.value || '',
      this.lifecycleCallbacks()
    );
  }

  checkAllUploaded(): void {
    if (!this.files.some((file) => file.status === 'uploading')) {
      this.isUploading = false;
      this.lifecycle.finishBatch(this.files, () => this.resetUploadState());
    }
  }

  clearCompleted(): void { this.files = this.files.filter((file) => file.status !== 'created'); }

  resetUploadState(): void {
    this.files = [];
    this.uploadForm.reset({ processImmediately: true, folderName: '', tags: [], roleId: '' });
    this.currentTag = '';
    this.lifecycle.reset();
    this.breadcrumbInputComponent?.reset();
  }

  private lifecycleCallbacks(): UploadLifecycleCallbacks {
    return {
      replace: (file) => this.replaceFile(file),
      showLimit: (error) => this.showPlanLimit(error),
      planLimitMessage: () => this.planLimitFriendlyMessage(),
      completed: () => this.checkAllUploaded()
    };
  }

  private filePresentation(fileName: string): DocumentTypePresentation | undefined {
    return this.documentTypes[fileName.split('.').pop()?.toLowerCase() || ''];
  }
}
